import React, { useEffect, useState } from 'react';
import { Button, Checkbox, DatePicker, Input, Modal, Radio, Select, Tooltip, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ClipboardCheck, Star, UserCog } from 'lucide-react';
import {
  CHECK_LEVEL, COMPLAINT_CATEGORY, CONTACT_CHANNEL, MANAGER, RESCHEDULE_WARN, SEVERITY, SYMPTOMS, agentsOf, appointmentsOf, assigneeOf,
  callCenterApi, complaintMetaOf, customerByPhone, isAftercareTask, isMedicalTask, isSurveyTask, needsEscalation, useCallCenter,
  type Callback, type CheckLevel, type ContactChannel, type Customer,
} from '../callCenterStore';
import { meName } from '../softphone';
import { CompleteCallbackButton } from './CallWidgets';

const QuickChips = ({ items, onPick }: { items: string[]; onPick: (v: string) => void }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map(q => (
      <button key={q} type="button" onClick={() => onPick(q)} className="text-xs px-2 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#3e91ab] cursor-pointer">{q}</button>
    ))}
  </div>
);

// ============================ Phiếu hỏi thăm sau dịch vụ ============================

/** Phiếu hỏi thăm sau dịch vụ: triệu chứng + mức đánh giá. Bất thường → việc gấp cho bác sĩ */
export function AftercareCheckButton({ customer, callback, children, buttonProps }: {
  customer: Customer; callback?: Callback | null; children?: React.ReactNode; buttonProps?: React.ComponentProps<typeof Button>;
}) {
  const s = useCallCenter();
  const done = appointmentsOf(s, customer.id).filter(a => a.status === 'done');
  const [open, setOpen] = useState(false);
  const [apptId, setApptId] = useState<string>();
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [level, setLevel] = useState<CheckLevel>('normal');
  const [channel, setChannel] = useState<ContactChannel>('call');
  const [note, setNote] = useState('');
  const [nextAt, setNextAt] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (!open) return;
    setApptId(callback?.appointmentId || done[0]?.id);
    setSymptoms([]); setLevel('normal'); setChannel('call'); setNote(''); setNextAt(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const ap = done.find(a => a.id === apptId);

  const save = () => {
    const err = callCenterApi.aftercareCheck({
      customerId: customer.id, callbackId: callback?.id, appointmentId: apptId, symptoms, level, channel, note, nextAt: nextAt ? nextAt.valueOf() : null, by: meName(),
    });
    if (err) { message.warning(err); return; }
    message.success(level === 'abnormal' ? `Đã ghi nhận · tạo việc gấp cho ${ap?.practitioner || 'bác sĩ phụ trách'}` : level === 'watch' ? 'Đã ghi nhận · hẹn hỏi thăm tiếp' : 'Đã ghi nhận khách hồi phục bình thường');
    setOpen(false);
  };

  return (
    <>
      <Button size="small" icon={<ClipboardCheck size={12} />} {...buttonProps} onClick={() => setOpen(true)}>{children || 'Phiếu hỏi thăm'}</Button>
      <Modal open={open} onCancel={() => setOpen(false)} width={560} zIndex={1050} title="Phiếu hỏi thăm sau dịch vụ"
        footer={[<Button key="c" onClick={() => setOpen(false)}>Huỷ</Button>, <Button key="s" type="primary" danger={level === 'abnormal'} onClick={save}>Lưu phiếu</Button>]}>
        <div className="space-y-4 text-sm pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-gray-500 w-28">Lần làm dịch vụ</span>
            <Select className="flex-1 min-w-[220px]" value={apptId} onChange={setApptId} placeholder="Chọn lần làm dịch vụ" notFoundContent="Khách chưa có lần làm dịch vụ"
              options={done.map(a => ({ value: a.id, label: `${dayjs(a.at).format('DD/MM/YYYY')} · ${a.service}${a.practitioner ? ` · ${a.practitioner}` : ''}` }))} />
          </div>
          {ap && <div className="text-xs text-gray-500 -mt-2 ml-0 sm:ml-[7.5rem]">Ngày thứ {Math.max(0, dayjs().startOf('day').diff(dayjs(ap.at).startOf('day'), 'day'))} sau dịch vụ</div>}
          {customer.medicalNote && <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">⚠ Tiền sử: {customer.medicalNote}</div>}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-gray-500 w-28">Hình thức</span>
            <Radio.Group size="small" optionType="button" buttonStyle="solid" value={channel} onChange={e => setChannel(e.target.value)}
              options={(Object.keys(CONTACT_CHANNEL) as ContactChannel[]).map(k => ({ value: k, label: CONTACT_CHANNEL[k] }))} />
          </div>
          <div>
            <div className="text-gray-500 mb-1.5">Triệu chứng khách báo</div>
            <Checkbox.Group value={symptoms} onChange={v => setSymptoms(v as string[])} options={SYMPTOMS} />
          </div>
          <div>
            <div className="text-gray-500 mb-1.5">Đánh giá</div>
            <Radio.Group value={level} onChange={e => setLevel(e.target.value)} className="flex flex-col gap-1.5">
              {(Object.keys(CHECK_LEVEL) as CheckLevel[]).map(k => <Radio key={k} value={k}><span className={`text-xs px-2 py-0.5 rounded border ${CHECK_LEVEL[k].badge}`}>{CHECK_LEVEL[k].label}</span></Radio>)}
            </Radio.Group>
          </div>
          {level === 'abnormal' && (
            <div className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 flex items-start gap-1.5">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" /> Tạo việc gấp (xử lý trong 1 giờ) cho {ap?.practitioner || 'bác sĩ phụ trách'} và đưa lên "Bước tiếp theo". Nếu khách sốt cao / chảy máu nhiều: hướng dẫn khách đến cơ sở y tế gần nhất ngay.
            </div>
          )}
          {level === 'watch' && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-gray-500 w-28">Hỏi thăm tiếp <span className="text-rose-500">*</span></span>
              <DatePicker showTime={{ format: 'HH:mm' }} format="HH:mm DD/MM/YYYY" value={nextAt} onChange={setNextAt} disabledDate={d => d.isBefore(dayjs(), 'day')}
                presets={[{ label: 'Ngày mai 10:00', value: dayjs().add(1, 'day').hour(10).minute(0).second(0) }, { label: 'Sau 3 ngày', value: dayjs().add(3, 'day').hour(10).minute(0).second(0) }]} />
            </div>
          )}
          <Input.TextArea value={note} onChange={e => setNote(e.target.value)} autoSize={{ minRows: 2, maxRows: 5 }}
            placeholder={level === 'abnormal' ? 'Bắt buộc: mô tả tình trạng (vị trí, mức độ, từ khi nào...)' : 'Ghi chú thêm: khách hài lòng, đã dặn chăm sóc tại nhà...'} />
        </div>
      </Modal>
    </>
  );
}

// ============================ Dời việc ============================

export function RescheduleTaskButton({ cb, buttonProps }: { cb: Callback; buttonProps?: React.ComponentProps<typeof Button> }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<Dayjs | null>(null);
  const [reason, setReason] = useState('');
  const count = cb.reschedules?.length || 0;
  const save = () => {
    const err = callCenterApi.rescheduleCallback(cb.id, at ? at.valueOf() : 0, reason, meName());
    if (err) { message.warning(err); return; }
    message.success(`Đã dời sang ${at!.format('HH:mm DD/MM')}`);
    setOpen(false);
  };
  return (
    <>
      <Button size="small" {...buttonProps} onClick={() => { setAt(null); setReason(''); setOpen(true); }}>Dời{count ? ` (${count})` : ''}</Button>
      <Modal open={open} onCancel={() => setOpen(false)} width={460} zIndex={1050} title="Dời việc gọi lại"
        footer={[<Button key="c" onClick={() => setOpen(false)}>Huỷ</Button>, <Button key="s" type="primary" disabled={!at || !reason.trim()} onClick={save}>Dời việc</Button>]}>
        <div className="space-y-3 text-sm pt-1">
          {count >= RESCHEDULE_WARN && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-start gap-1.5">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" /> Việc này đã dời {count} lần. Cân nhắc đổi kênh liên hệ (Zalo/SMS) hoặc đóng việc với kết quả rõ ràng.
            </div>
          )}
          <DatePicker className="w-full" showTime={{ format: 'HH:mm' }} format="HH:mm DD/MM/YYYY" value={at} onChange={setAt} disabledDate={d => d.isBefore(dayjs(), 'day')}
            presets={[{ label: 'Sau 1 giờ', value: dayjs().add(1, 'hour').startOf('minute') }, { label: 'Sáng mai 9:00', value: dayjs().add(1, 'day').hour(9).minute(0).second(0) }, { label: 'Chiều mai 15:00', value: dayjs().add(1, 'day').hour(15).minute(0).second(0) }]} />
          <div>
            <div className="text-gray-500 mb-1">Lý do <span className="text-rose-500">*</span></div>
            <Input.TextArea value={reason} onChange={e => setReason(e.target.value)} autoSize={{ minRows: 2 }} placeholder="VD: Khách bận, hẹn gọi lại buổi chiều" />
            <div className="mt-1.5"><QuickChips items={['Khách bận, hẹn giờ khác', 'Không nghe máy', 'Khách đang đi công tác']} onPick={setReason} /></div>
          </div>
          {!!count && (
            <div className="text-xs text-gray-500 space-y-0.5">
              {cb.reschedules!.map((r, i) => <div key={i}>Lần {i + 1}: {dayjs(r.from).format('HH:mm DD/MM')} → {dayjs(r.to).format('HH:mm DD/MM')} · {r.by} — {r.reason}</div>)}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

// ============================ Người phụ trách ============================

/** Hiển thị + giao lại người phải làm việc */
export function AssigneeSelect({ cb }: { cb: Callback }) {
  const s = useCallCenter();
  const current = assigneeOf(s, cb);
  const options = Array.from(new Set([current, ...agentsOf(s), MANAGER, meName()])).map(v => ({ value: v, label: v }));
  return (
    <Tooltip title="Người phải thực hiện — bấm để giao lại">
      <Select size="small" variant="borderless" className="min-w-[130px] !text-xs" value={current} popupMatchSelectWidth={false}
        suffixIcon={<UserCog size={12} />} options={options}
        onChange={v => { callCenterApi.reassignCallback(cb.id, v); message.success(`Đã giao cho ${v}`); }} />
    </Tooltip>
  );
}

// ============================ Nhãn loại việc ============================

export function TaskBadges({ cb }: { cb: Callback }) {
  const meta = complaintMetaOf(cb);
  const badge = (text: string, cls: string) => <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cls}`}>{text}</span>;
  return (
    <>
      {isMedicalTask(cb) && badge('BẤT THƯỜNG', 'bg-red-600 text-white')}
      {meta && badge('KHIẾU NẠI', 'bg-rose-100 text-rose-600')}
      {meta && badge(`${COMPLAINT_CATEGORY[meta.category]} · ${SEVERITY[meta.severity].label}`, SEVERITY[meta.severity].badge)}
      {meta?.level === 2 && badge('ĐÃ CHUYỂN QUẢN LÝ', 'bg-purple-100 text-purple-700')}
      {meta?.reopenedFrom && badge('MỞ LẠI', 'bg-orange-100 text-orange-700')}
      {isAftercareTask(cb) && badge('SAU DV', 'bg-teal-100 text-teal-700')}
      {isSurveyTask(cb) && badge('KHẢO SÁT', 'bg-amber-100 text-amber-700')}
      {(cb.reschedules?.length || 0) >= RESCHEDULE_WARN && badge(`DỜI ${cb.reschedules!.length} LẦN`, 'bg-gray-200 text-gray-700')}
    </>
  );
}

// ============================ Nút hoàn thành theo loại việc ============================

/**
 * Đóng việc đúng quy trình:
 * - Hỏi thăm sau DV → phiếu hỏi thăm
 * - Khảo sát → mở form khảo sát (onSurvey) hoặc trang hồ sơ khách
 * - Khiếu nại quá hạn → thêm nút chuyển quản lý
 * - Còn lại → nhập kết quả (khiếu nại hỏi thêm mức hài lòng)
 */
export function TaskDoneButton({ cb, onSurvey, buttonProps }: { cb: Callback; onSurvey?: (appointmentId?: string) => void; buttonProps?: React.ComponentProps<typeof Button> }) {
  const s = useCallCenter();
  const navigate = useNavigate();
  const customer = customerByPhone(s, cb.phone);
  if (isAftercareTask(cb) && customer) return <AftercareCheckButton customer={customer} callback={cb} buttonProps={buttonProps} />;
  if (isSurveyTask(cb)) {
    return (
      <Button size="small" icon={<Star size={12} />} {...buttonProps}
        onClick={() => (onSurvey ? onSurvey(cb.appointmentId) : customer && navigate(`/customer-demo/detail?id=${customer.id}&survey=${cb.appointmentId || ''}`))}>Khảo sát</Button>
    );
  }
  return (
    <>
      {needsEscalation(cb) && (
        <Button size="small" danger {...buttonProps} onClick={() => { callCenterApi.escalateComplaint(cb.id, meName()); message.success(`Đã chuyển ${MANAGER} xử lý`); }}>Chuyển quản lý</Button>
      )}
      <CompleteCallbackButton id={cb.id} complaint={!!complaintMetaOf(cb)} buttonProps={buttonProps} />
    </>
  );
}
